import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from '@angular/fire/auth';
import { AuthService } from '../../core/authentication/auth';
import { LoaderService } from '../../core/ui/loader';
import { AlertService } from '../../core/ui/alert';

function passwordComplexityValidator(control: AbstractControl): ValidationErrors | null {
	const value = control.value;

	if(!value) {
		return null;
	}

	const errors: { [key: string]: any } = {};
	const minLength = 8;

	if(value.length < minLength) {
		errors['minLength'] = { requiredLength: minLength, actualLength: value.length };
	}

	if(!/[a-z]/.test(value)) {
		errors['hasLowercase'] = true;
	}

	if(!/[A-Z]/.test(value)) {
		errors['hasUppercase'] = true;
	}

	if(!/[0-9]/.test(value)) {
		errors['hasNumeric'] = true;
	}

	if(!/[\^$*.[\]{}()?\"!@#%&/\\,><':;|_~`]/.test(value)) {
		errors['hasSpecial'] = true;
	}

	return Object.keys(errors).length > 0 ? errors : null;
}

function passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
	const newPasswordControl = group.get('newPassword');
	const confirmPasswordControl = group.get('confirmPassword');
	const newPassword = newPasswordControl?.value;
	const confirmPassword = confirmPasswordControl?.value;

	if(!confirmPasswordControl) {
		return null;
	}

	if(!newPassword || !confirmPassword) {
		const currentErrors = confirmPasswordControl.errors || {};
		if(currentErrors['passwordMismatch']) {
			delete currentErrors['passwordMismatch'];
			confirmPasswordControl.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
		}
		return null;
	}

	if(newPassword !== confirmPassword) {
		const currentErrors = confirmPasswordControl.errors || {};
		confirmPasswordControl.setErrors({ ...currentErrors, passwordMismatch: true });
		return { passwordMismatch: true };
	}

	const currentErrors = confirmPasswordControl.errors || {};
	if(currentErrors['passwordMismatch']) {
		delete currentErrors['passwordMismatch'];
		confirmPasswordControl.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
	}
	return null;
}

@Component({
	selector: 'app-my-account',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule],
	templateUrl: './my-account.html',
	styleUrl: './my-account.css'
})
export class MyAccount implements OnInit {
	myAccountForm!: FormGroup;
	hideCurrentPassword = true;
	hideNewPassword = true;
	hideConfirmPassword = true;

	constructor(private formBuilder: FormBuilder, private authService: AuthService, private loaderService: LoaderService, private alertService: AlertService) {}

	ngOnInit(): void {
		this.myAccountForm = this.formBuilder.group({
			currentPassword: ['', [Validators.required]],
			newPassword: ['', [Validators.required, passwordComplexityValidator]],
			confirmPassword: ['', [Validators.required]]
		}, { validators: passwordMatchValidator });
	}

	get currentPasswordControl() {
		return this.myAccountForm.get('currentPassword');
	}

	get newPasswordControl() {
		return this.myAccountForm.get('newPassword');
	}

	get confirmPasswordControl() {
		return this.myAccountForm.get('confirmPassword');
	}

	get hasPasswordMismatch(): boolean {
		return !!(this.confirmPasswordControl?.errors?.['passwordMismatch'] && (this.confirmPasswordControl?.dirty || this.confirmPasswordControl?.touched));
	}

	toggleCurrentPasswordVisibility(): void {
		this.hideCurrentPassword = !this.hideCurrentPassword;
	}

	toggleNewPasswordVisibility(): void {
		this.hideNewPassword = !this.hideNewPassword;
	}

	toggleConfirmPasswordVisibility(): void {
		this.hideConfirmPassword = !this.hideConfirmPassword;
	}

	async onSubmit(): Promise<void> {
		this.myAccountForm.markAllAsTouched();

		if(!this.myAccountForm.valid) {
			return;
		}

		if(!window.confirm('¿Confirmas el cambio de contraseña?')) {
			return;
		}

		const { currentPassword, newPassword } = this.myAccountForm.value;

		try {
			this.loaderService.show();
			const authInstance = this.authService.authInstance;
			const user = authInstance.currentUser;

			if(!user || !user.email) {
				this.alertService.error('No se pudo obtener la sesión del usuario.', 'Error');
				this.loaderService.hide();
				return;
			}

			const credential = EmailAuthProvider.credential(user.email, currentPassword);
			await reauthenticateWithCredential(user, credential);
			await updatePassword(user, newPassword);

			this.alertService.success('Tu contraseña se actualizó correctamente.', 'Éxito');
			this.myAccountForm.reset();
			this.loaderService.hide();
		} catch (error: any) {
			let errorMessage = 'Ocurrió un error inesperado al cambiar la contraseña.';

			switch (error.code) {
				case 'auth/wrong-password':
				case 'auth/invalid-credential':
					errorMessage = 'La contraseña actual es incorrecta.';
				break;
				case 'auth/too-many-requests':
					errorMessage = 'Has realizado demasiados intentos. Inténtalo más tarde.';
				break;
				case 'auth/network-request-failed':
					errorMessage = 'Problemas de conexión. Verifica tu internet.';
				break;
				case 'auth/weak-password':
					errorMessage = 'La nueva contraseña no cumple los requisitos de seguridad.';
				break;
				case 'auth/requires-recent-login':
					errorMessage = 'Por seguridad, vuelve a iniciar sesión e inténtalo de nuevo.';
				break;
				default:
					errorMessage = 'Ocurrió un error inesperado al cambiar la contraseña.';
				break;
			}

			this.alertService.error(errorMessage, 'Error');
			this.loaderService.hide();
		}
	}
}
